import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { Tenant } from '../../types/tenants';
import { tenantsApi } from '../../api/tenantsApi';

/**
 * Tenants root, loaded once per session and shared by every mounted consumer
 * through a module-level store (like `useUserNames` caches user names) — no
 * provider in App.js, nothing runs until the first consumer mounts. `refresh()`
 * updates every mounted consumer, so a tenant created inline in the lease
 * modal shows up in the page behind it. The store empties on logout.
 */

interface TenantsState {
    tenants: Tenant[];
    loading: boolean;
    loaded: boolean;
    error: string | null;
}

let state: TenantsState = { tenants: [], loading: false, loaded: false, error: null };
const listeners = new Set<() => void>();
let inFlight: Promise<void> | null = null;
let authWatched = false;

const setState = (patch: Partial<TenantsState>) => {
    state = { ...state, ...patch };
    listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};

const getSnapshot = () => state;

const fetchTenants = (): Promise<void> => {
    if (inFlight) return inFlight;
    setState({ loading: true, error: null });
    inFlight = tenantsApi.getAll()
        .then(tenants => setState({ tenants, loaded: true, loading: false }))
        .catch(err => {
            console.error('Error fetching tenants:', err);
            setState({ loading: false, error: 'Kunde inte ladda registret' });
        })
        .finally(() => { inFlight = null; });
    return inFlight;
};

const watchAuth = () => {
    if (authWatched) return;
    authWatched = true;
    onAuthStateChanged(auth, user => {
        if (!user) {
            state = { tenants: [], loading: false, loaded: false, error: null };
            listeners.forEach(listener => listener());
        } else if (!state.loaded && listeners.size > 0) {
            fetchTenants();
        }
    });
};

export interface UseTenantsReturn {
    /** Tenants to list and pick from: anonymised ("Borttagen hyresgäst") tenants are left out. */
    tenants: Tenant[];
    /** Every tenant incl. anonymised ones, for callers that must show history. */
    allTenants: Tenant[];
    /** Keyed over `allTenants`, so leases and queue logs still resolve an anonymised tenant's name. */
    tenantsByKey: Record<string, Tenant>;
    loading: boolean;
    error: string | null;
    /** Synchronous name lookup; 'Okänd hyresgäst' when the key is unknown. */
    getTenantName: (tenantKey: string | undefined) => string;
    refresh: () => Promise<void>;
}

export const useTenants = (): UseTenantsReturn => {
    const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    useEffect(() => {
        watchAuth();
        if (!state.loaded && !state.loading && auth.currentUser) {
            fetchTenants();
        }
    }, []);

    const tenants = useMemo(() => snapshot.tenants.filter(tenant => !tenant.anonymized), [snapshot.tenants]);

    const tenantsByKey = useMemo(() => {
        const map: Record<string, Tenant> = {};
        for (const tenant of snapshot.tenants) map[tenant.key] = tenant;
        return map;
    }, [snapshot.tenants]);

    const getTenantName = useCallback(
        (tenantKey: string | undefined) => (tenantKey && tenantsByKey[tenantKey]?.name) || 'Okänd person',
        [tenantsByKey]
    );

    const refresh = useCallback(() => fetchTenants(), []);

    return {
        tenants,
        allTenants: snapshot.tenants,
        tenantsByKey,
        loading: snapshot.loading || (!snapshot.loaded && !snapshot.error),
        error: snapshot.error,
        getTenantName,
        refresh,
    };
};
